<template>
  <b-container>
    <b-row>
      <b-col cols="12">
        <b-button variant="warning" size="sm"
          v-if="this.newDataAvailable === true"
          class="btn-reload-demands-and-purchase-offers"
          @click="reloadData()"
        >
          New data available. Click here to reload.
          <b-icon-arrow-repeat/>
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12" lg="7" xl="8"><h1>Demanded Watches</h1></b-col>
      <b-col cols="12" lg="5" xl="4" v-if="status === localConstants.STATUS_FINISHED_LOADING" class="text-right">
        Last update : {{ lastUpdate }}
      </b-col>
    </b-row>
    <div v-if="status === localConstants.STATUS_LOADING">
      <b-row>
        <b-col cols="12" class="text-center col-loading">
          <h3>Loading Demanded watches <b-spinner/></h3>
        </b-col>
      </b-row>
    </div>
    <div v-if="status === localConstants.STATUS_ERROR_LOADING">
      <b-row class="justify-content-center">
        <b-col cols="8" class="text-center col-loading">
          <div class="alert alert-danger">
            Temporary Error during loading. Please try to refresh the page and see if the error persists.
          </div>
        </b-col>
      </b-row>
    </div>
    <transition name="fade">
      <div v-if="status === localConstants.STATUS_FINISHED_LOADING">
        <b-row class="align-items-end">
          <b-col cols="12" md="4">
            <label for="textFilter">Text</label>
            <input
              type="text"
              v-model="textFilter"
              id="textFilter"
              class="form-control form-control-sm"
              placeholder="Search in Demanded Watches"
            />
          </b-col>
          <b-col cols="12" md="3" class="select-brand">
            <label for="brandSelected">Brands</label>
            <select
              title="Select Brand"
              v-model="brandSelected"
              id="brandSelected"
              class="form-control form-control-sm"
            >
              <option value="">Select a brand</option>
              <option v-for="brandOption in brandOptions" :value="brandOption" :key="brandOption">
                {{ brandOption }}
              </option>
            </select>
          </b-col>
          <b-col cols="12" md="3" class="btn-group" role="group">
            <b-button variant="secondary" size="sm"
              @click="resetFilter()"
              :disabled="!filterActive"
            >
              Reset Filter <b-icon-arrow-counterclockwise/>
            </b-button>
            <b-button variant="info" size="sm" @click="toggleView()">
              <template v-if="rowViewActive"><b-icon-grid-fill/> Show as grid</template>
              <template v-else><b-icon-list/> Show as list</template>
            </b-button>
          </b-col>
          <b-col cols="12" md="2" class="text-right">
            <br />
            Showing <strong>{{ numberOfFilteredDemands }}</strong> of {{ numberOfTotalDemands }} watches.
          </b-col>
        </b-row>
        <!--
          Some extra padding, due to the col-bordered below, so the borders align with the heading
        -->

        <div v-if="rowViewActive" class="demand-table">
          <b-table striped hover show-empty :items="filteredDemands" :fields="fields">
            <template v-slot:cell(image)="data">
              <b-img-lazy :id="data.item.demandId" :src="getThumbnail(data.item.imageUrl, 10)" alt="" />
              <b-tooltip :target="data.item.demandId" placement="left">
                <b-img :src="getThumbnail(data.item.imageUrl, 100)" fluid />
              </b-tooltip>
            </template>
            <template v-slot:cell(actions)="data">
              <b-button size="sm" variant="primary" @click="openModal(data.item)">Make an Offer</b-button>
              <watch-detail-modal
                @close="closeModal(data.item)"
                v-if="showModal"
                :watch="data.item"
                :type="'demand'"
                :btn-text="'Make an Offer'"
              ></watch-detail-modal>
            </template>
          </b-table>
        </div>
        <template v-else>
          <b-col cols="12">
            <hr />
            <b-row>
              <watch-in-grid
                :watch="demand"
                type="demand"
                btn-text="Make an Offer"
                v-for="demand in filteredDemands"
                :key="demand.id"
                class="col-6 col-md-3 col-lg-2 col-bordered"
              ></watch-in-grid>
            </b-row>
          </b-col>
        </template>
      </div>
    </transition>
  </b-container>
</template>

<script>
import store from 'src/store/index';
import WatchInGrid from 'Components/WatchInGrid';
import WatchDetailModal from 'Components/WatchDetailModal';
import { timeFormatMixin } from 'Mixins/timeFormatMixin';

export default {
  components: {
    WatchInGrid,
    WatchDetailModal,
  },
  mixins: [timeFormatMixin],
  data: function() {
    return {
      status: '',
      brandSelected: '',
      textFilter: '',
      demands: [],
      lastDemandUpdate: '',
      updateInterval: null,
      newDataAvailable: false,
      localConstants: {
        STATUS_LOADING: 'loading',
        STATUS_ERROR_LOADING: 'errorLoading',
        STATUS_FINISHED_LOADING: 'finishedLoading',
      },
      rowViewActive: false,
      showModal: false,
      fields: ['image', 'referenceNumber', 'amount', 'brand', 'model', 'actions'],
    };
  },
  computed: {
    filterActive() {
      return this.textFilter !== '' || this.brandSelected !== '';
    },
    numberOfTotalDemands() {
      return this.demands.length;
    },
    numberOfFilteredDemands() {
      return this.filteredDemands.length;
    },
    filteredDemands() {
      return this.demands.filter((demand) => {
        let textFilter = this.textFilter.toLowerCase();
        let watchBrand = demand.brand.toLowerCase();

        if (
          !Array.isArray(demand.condition) &&
          typeof demand.condition !== 'object' &&
          demand.condition !== undefined &&
          demand.condition.toLowerCase() !== 'new'
        ) {
          return false;
        }

        if (
          demand.category !== undefined &&
          (demand.category.toLowerCase() === 'cpst' || demand.category.toLowerCase() === 'c2c')
        ) {
          return false;
        }

        if (watchBrand.indexOf(this.brandSelected.toLowerCase()) === -1) {
          return false;
        }

        if (watchBrand.indexOf(textFilter) !== -1) {
          return true;
        }

        if (demand.model.toLowerCase().indexOf(textFilter) !== -1) {
          return true;
        }

        if (demand.referenceNumber.toLowerCase().indexOf(textFilter) !== -1) {
          return true;
        }

        if (demand.variantKey.toLowerCase().indexOf(textFilter) !== -1) {
          return true;
        }
      });
    },
    brandOptions() {
      let brands = [];
      this.demands.forEach((demand) => {
        if (!brands.includes(demand.brand)) {
          brands.push(demand.brand);
        }
      });
      return brands.sort();
    },
    lastUpdate() {
      return this.lastDemandUpdate !== null ? this.formattedTime(this.lastDemandUpdate) : '';
    },
  },
  methods: {
    resetFilter() {
      this.brandSelected = '';
      this.textFilter = '';
    },
    checkForUpdates() {
      store.dispatch('checkForUpdates').then((response) => {
        if (response.data.lastUpdate !== undefined) {
          if (this.lastDemandUpdate < response.data.lastUpdate) {
            this.newDataAvailable = true;
          }
        }
      });
    },
    reloadData() {
      this.status = this.localConstants.STATUS_LOADING;
      store
        .dispatch('loadDemands')
        .then((response) => {
          if (response.data.demands === undefined || response.data.lastUpdate === undefined) {
            this.status = this.localConstants.STATUS_ERROR_LOADING;
          } else {
            this.status = this.localConstants.STATUS_FINISHED_LOADING;
            this.demands = response.data.demands;
            this.lastDemandUpdate = response.data.lastUpdate;
            if (true === this.newDataAvailable) {
              this.$ga.event({
                eventCategory: 'DemandOverview',
                eventAction: 'click_refresh_demands',
                eventLabel: 'Refresh demand list',
              });
            }
          }
        })
        .catch(() => {
          this.status = this.localConstants.STATUS_ERROR_LOADING;
        });
      this.newDataAvailable = false;
    },
    toggleView() {
      this.rowViewActive = !this.rowViewActive;
      this.reloadData();
    },
    openModal(item) {
      this.showModal = true;
      this.$ga.event({
        eventCategory: this.type === 'watch' ? 'B2BOverview' : 'DemandOverview',
        eventAction: this.type === 'watch' ? 'click_watch' : 'click_demand',
        eventLabel:
          this.type === 'watch'
            ? 'Open WatchDetail for watch ' + item['itemId']
            : 'Open DemandDetail for demand ' + item['itemId'],
      });
    },
    closeModal(item) {
      this.showModal = false;
      this.$ga.event({
        eventCategory: this.type === 'watch' ? 'WatchDetail' : 'DemandDetail',
        eventAction: this.type === 'watch' ? 'click_watch_background' : 'click_demand_background',
        eventLabel:
          this.type === 'watch'
            ? 'Close WatchDetail for watch ' + item['itemId']
            : 'Close DemandDetail for demand ' + item['itemId'],
      });
    },
    getThumbnail(url, width) {
      return url !== ''
        ? url.replace('cdn-products.chronext.com', 'chronexttime.imgix.net') +
            '?w=' +
            width +
            '&auto=format&fm=jpg&q=55&usm=50&usmrad=1.5&dpr=2&fit=clamp'
        : '';
    },
  },
  mounted() {
    this.reloadData();
    this.updateInterval = setInterval(this.checkForUpdates, 30000);
  },
  beforeDestroy() {
    clearInterval(this.updateInterval);
  },
};
</script>

<style lang="scss" scoped>
h1 {
  @media all and (max-device-width: 576px) {
    font-size: 1.6rem;
  }
}

.col-loading {
  padding: 30px 0;
}

.select-brand {
  @media all and(max-device-width: 576px) {
    margin-bottom: 10px;
  }
}

.col-bordered {
  padding: 0;
  border: 1px solid #cccccc;
  &:hover,
  &:focus,
  &:active {
    cursor: pointer;
    border-color: #777777;
    box-shadow: 0 0 10px 0 rgba(50, 50, 50, 0.5);
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.btn-reload-demands-and-purchase-offers {
  position: fixed;
  z-index: 100;
  right: 40px;
  top: 50px;
}

.demand-table {
  padding-top: 2rem;
}
</style>
